/**
 * Dollars in, cents out — the one conversion, in one place.
 *
 * Money is integer cents everywhere (dev.md), and the only place a decimal is
 * allowed to exist is between an owner's finger and this function. `Settings`
 * has been doing `Math.round(Number(price) * 100)` inline since MVP 2; the till
 * cannot, because a till has to distinguish "they typed nothing yet" from "they
 * typed a zero" from "they typed something that is not a number" — and
 * `Number('')` is `0`, which collapses the first two into a free massage.
 *
 * So: `null` for "not a number I can use", a whole number of cents otherwise.
 * Callers decide what a `null` means in their context — a blank discount box is
 * "no discount", a blank price box is "the catalog's".
 */
export function centsFromInput(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  if (!/^-?\d*([.,]\d{0,2})?$/.test(trimmed)) return null;
  const value = Number(trimmed.replace(',', '.'));
  if (!Number.isFinite(value)) return null;
  // `12.34 * 100` is `1233.9999999999998` in binary floating point. Rounding is
  // not a nicety here: it is the difference between a drawer that balances and
  // a cent that goes missing on one line in every few hundred.
  return Math.round(value * 100);
}

/** Cents back into the box, for an editable price field. */
export function inputFromCents(cents: number): string {
  return (cents / 100).toFixed(2);
}

/**
 * One keypad press against the text already in a box (B4 — *"almost just like a
 * calculator"*).
 *
 * The till's number pad is not a shortcut for the system keyboard; on an iPad at
 * a counter it is the *only* comfortable way to type a price, because the
 * software keyboard takes half the screen and hides the running total. So the
 * pad edits the same strings the text inputs hold, and `centsFromInput` above
 * stays the one conversion — there is no second parser here and no numeric state
 * behind the text to drift from it.
 *
 * The rules are the ones a cheap calculator has, chosen because they are the
 * ones a thumb already expects:
 *
 *  - `'⌫'` removes the last character; on an empty box it is a no-op rather than
 *    an error, because a thumb resting on backspace is not a mistake.
 *  - `'.'` is ignored when `decimals` is false (a quantity) and when the box
 *    already has one, so `1..5` cannot be typed into existence.
 *  - Anything that would leave more than two decimal places is **dropped**, not
 *    rounded: a till that quietly turned `19.999` into `20.00` while the owner
 *    was still typing would be a till that changed a price behind her.
 *  - A leading `.` becomes `0.`, which is what every keypad in the world does.
 *
 * It returns the new text and never throws, so a caller is `setState(apply(...))`
 * and nothing else.
 */
export function applyKeypad(current: string, key: string, decimals: boolean): string {
  if (key === '⌫') return current.slice(0, -1);
  if (key === '.') {
    if (!decimals || current.includes('.')) return current;
    return current === '' ? '0.' : `${current}.`;
  }
  if (!/^[0-9]$/.test(key)) return current;
  const next = `${current}${key}`;
  const dot = next.indexOf('.');
  if (dot >= 0 && next.length - dot - 1 > 2) return current;
  // A leading zero is dropped so "0" then "5" is five rather than "05" — but
  // "0." survives, because that is somebody typing a value under a dollar.
  if (current === '0') return key;
  return next;
}
